import React, { useState, useEffect } from 'react';
import { Star, ChefHat, MessageCircle, RefreshCw, Share2, Clock, ArrowLeft, Utensils, Calendar } from 'lucide-react';
import api from '../api/api';
import Seo from '../components/Seo';

export default function RecipeRatingPage() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [wouldMakeAgain, setWouldMakeAgain] = useState(null);
  const [perceivedDifficulty, setPerceivedDifficulty] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const seo = (
    <Seo
      title="Noter mes recettes"
      description="Donnez votre avis sur les recettes YAQAM que vous avez testées et aidez-nous à les améliorer."
      breadcrumb
      noindex
    />
  );

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        setLoading(true);
        const response = await api.post('/api/meals/recipes', {
          status: 'finished',
          $or: [
            { userRanking: { $exists: false } },
            { userRanking: null }
          ]
        });
        setRecipes(response.data.recipes);
        setLoading(false);
      } catch (err) {
        setError('Erreur lors du chargement des recettes');
        setLoading(false);
      }
    };
    fetchRecipes();
  }, []);

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Veuillez donner une note à la recette');
      return;
    }

    const evaluationData = {
      date: new Date(),
      recipeRank: rating,
      difficultyRank: ['Très facile', 'Facile', 'Moyen', 'Difficile', 'Très difficile'].indexOf(perceivedDifficulty) + 1,
      replay: wouldMakeAgain,
      commentString: feedback
    };

    try {
      const newRecipe = { ...selectedRecipe, userRanking: evaluationData };
      await Promise.all([
        api.post(`/api/users/update-user-list`, {
          recipe: newRecipe,
          typeList: "ranking"
        }),
        api.post(`/api/meals/update-recipe`, {
          recipe: newRecipe
        })
      ]);

      setRecipes(prevRecipes => prevRecipes.filter(recipe => recipe.id !== selectedRecipe.id));
      setSubmitted(true);
    } catch (err) {
      alert("Erreur lors de la soumission de l'évaluation");
      console.error(err);
    }
  };

  const handleReset = () => {
    setRating(0);
    setHoverRating(0);
    setFeedback('');
    setWouldMakeAgain(null);
    setPerceivedDifficulty('');
    setSubmitted(false);
    setSelectedRecipe(null);
  };

  const handleRecipeSelect = (recipe) => {
    setSelectedRecipe(recipe);
    setRating(0);
    setHoverRating(0);
    setFeedback('');
    setWouldMakeAgain(null);
    setPerceivedDifficulty('');
  };

  const getComplexityLabel = (complexity) => {
    switch(complexity) {
      case 1: return 'Très facile';
      case 2: return 'Facile';
      case 3: return 'Moyen';
      case 4: return 'Difficile';
      case 5: return 'Très difficile';
      default: return 'Non défini';
    }
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 1: return 'bg-[#87A96B]/20 text-[#87A96B]';
      case 2: return 'bg-[#87A96B]/30 text-[#87A96B]';
      case 3: return 'bg-[#E67E22]/20 text-[#E67E22]';
      case 4: return 'bg-[#E67E22]/30 text-[#E67E22]';
      case 5: return 'bg-[#E67E22] text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`;
    }
    return `${mins}min`;
  };

  const formatDate = (date) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(date).toLocaleDateString('fr-FR', options);
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInMs = now - new Date(date);
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) {
      return 'Il y a moins d\'une heure';
    } else if (diffInHours < 24) {
      return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
    } else if (diffInDays < 7) {
      return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
    } else {
      return formatDate(date);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
            <div className="w-20 h-20 bg-[#87A96B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <ChefHat className="w-10 h-10 text-[#87A96B]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-4">
              Merci pour votre évaluation !
            </h1>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Votre avis nous aide à améliorer nos recettes et à aider d'autres cuisiniers.
            </p>
            
            {wouldMakeAgain === true && selectedRecipe?.variantSuggestions?.length > 0 && (
              <div className="bg-[#87A96B]/10 rounded-lg p-4 mb-6 border border-[#87A96B]/30">
                <h3 className="font-semibold text-[#87A96B] mb-2 text-sm sm:text-base">Variantes à essayer :</h3>
                <div className="text-sm text-[#2C3E50] space-y-1">
                  {selectedRecipe.variantSuggestions.map((variant, index) => (
                    <div key={index}>• {variant}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center">
              <button
                onClick={handleReset}
                className="px-6 py-3 bg-[#87A96B] text-white rounded-full hover:bg-[#87A96B]/90 transition-colors shadow-md text-sm sm:text-base font-medium"
              >
                Évaluer une autre recette
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedRecipe) {
    return (
      <div className="min-h-screen bg-white p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="relative h-48 sm:h-64 bg-[#2C3E50]">
              <img 
                src={
                      selectedRecipe.imgUrlArray.find(x => x.includes('/recipe')) ??
                      selectedRecipe.imgUrlArray.find(x => x.includes('/suggestion')) ??
                      "/img/placeholder-food.jpg"
                    }
                alt={selectedRecipe.title}
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-[#2C3E50] bg-opacity-40 flex items-center justify-center">
                <div className="text-center text-white">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{selectedRecipe.title}</h1>
                  <p className="text-sm sm:text-lg opacity-90">Comment s'est passée votre expérience culinaire ?</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRecipe(null)}
                className="absolute top-4 left-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
              >
                <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-6 sm:p-8">
              <div className="space-y-6 sm:space-y-8">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600 mb-2 space-y-2 sm:space-y-0">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      Temps total: {formatTime(selectedRecipe.prepTime + selectedRecipe.cookTime)}
                    </div>
                    <div className="flex items-center">
                      <ChefHat className="w-4 h-4 mr-1" />
                      {getComplexityLabel(selectedRecipe.complexity)}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-1" />
                    Réalisée le {formatDate(selectedRecipe.updatedAt)}
                  </div>
                </div>

                <div className="text-center">
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2C3E50] mb-2">
                    Notez cette recette
                  </h2>
                  <div className="bg-[#87A96B]/10 border border-[#87A96B]/30 rounded-lg p-4 mb-4 sm:mb-6">
                    <p className="text-sm text-[#2C3E50]">
                      💡 <strong>Pourquoi noter ?</strong> Votre évaluation nous aide à mieux comprendre vos préférences et à vous suggérer des recettes qui vous plairont davantage !
                    </p>
                  </div>
                  <div className="flex justify-center space-x-2 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="focus:outline-none focus:ring-2 focus:ring-[#87A96B] rounded"
                      >
                        <Star
                          className={`w-8 h-8 sm:w-10 sm:h-10 transition-colors ${
                            star <= (hoverRating || rating)
                              ? 'text-[#E67E22] fill-[#E67E22]'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {rating === 0 ? 'Cliquez sur les étoiles pour noter' : 
                     rating === 1 ? 'Pas terrible' :
                     rating === 2 ? 'Moyen' :
                     rating === 3 ? 'Bien' :
                     rating === 4 ? 'Très bien' :
                     'Excellent !'}
                  </p>
                </div>

                <div className="text-center">
                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: selectedRecipe.title,
                          text: `J'ai testé cette recette : ${selectedRecipe.title}`,
                          url: window.location.href
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Lien copié dans le presse-papier !');
                      }
                    }}
                    className="inline-flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#87A96B] text-white rounded-full hover:bg-[#87A96B]/90 transition-colors text-sm sm:text-base font-medium"
                  >
                    <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Partager cette recette</span>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3E50] mb-3">
                    <ChefHat className="inline w-4 h-4 mr-1" />
                    Quelle difficulté avez-vous ressentie ?
                  </label>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {['Très facile', 'Facile', 'Moyen', 'Difficile', 'Très difficile'].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setPerceivedDifficulty(level)}
                        className={`px-3 sm:px-4 py-2 rounded-full border transition-colors text-sm sm:text-base ${
                          perceivedDifficulty === level
                            ? 'bg-[#87A96B] text-white border-[#87A96B]'
                            : 'bg-white text-[#2C3E50] border-gray-300 hover:bg-[#87A96B]/10'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3E50] mb-3">
                    <RefreshCw className="inline w-4 h-4 mr-1" />
                    Referiez-vous cette recette ?
                  </label>
                  <div className="flex gap-3 sm:gap-4">
                    <button
                      type="button"
                      onClick={() => setWouldMakeAgain(true)}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full border transition-colors text-sm sm:text-base ${
                        wouldMakeAgain === true
                          ? 'bg-[#87A96B] text-white border-[#87A96B]'
                          : 'bg-white text-[#2C3E50] border-gray-300 hover:bg-[#87A96B]/10'
                      }`}
                    >
                      👍 Oui, avec plaisir
                    </button>
                    <button
                      type="button"
                      onClick={() => setWouldMakeAgain(false)}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full border transition-colors text-sm sm:text-base ${
                        wouldMakeAgain === false
                          ? 'bg-[#2C3E50] text-white border-[#2C3E50]'
                          : 'bg-white text-[#2C3E50] border-gray-300 hover:bg-[#2C3E50]/10'
                      }`}
                    >
                      👎 Non, pas vraiment
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                    <MessageCircle className="inline w-4 h-4 mr-1" />
                    Vos commentaires (optionnel)
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Partagez vos impressions, modifications apportées, conseils pour améliorer cette recette et les prochaines..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] resize-none text-sm sm:text-base transition-colors"
                    rows={4}
                  />
                </div>

                {selectedRecipe.tags?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                      Tags de cette recette
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecipe.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-[#87A96B]/20 text-[#87A96B] rounded-full text-sm font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setSelectedRecipe(null)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-[#2C3E50] text-white rounded-full hover:bg-[#2C3E50]/90 transition-colors text-sm sm:text-base font-medium"
                  >
                    Retour à la liste
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-[#87A96B] to-[#87A96B]/90 text-white rounded-full hover:from-[#87A96B]/90 hover:to-[#87A96B]/80 transition-all shadow-lg hover:shadow-xl text-sm sm:text-base font-medium"
                  >
                    Soumettre mon évaluation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {seo}
      <div className="min-h-screen bg-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] mb-4">
            Évaluez vos recettes
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Partagez votre expérience culinaire et aidez-nous à améliorer nos recettes
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#87A96B]"></div>
            <p className="mt-4 text-gray-600 text-sm sm:text-base">Chargement des recettes...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm sm:text-base">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {recipes.map((recipe) => (
              <div
                key={recipe.id}
                onClick={() => handleRecipeSelect(recipe)}
                className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl border border-gray-100"
              >
                <div className="relative h-48">
                  <img
                    src={
                      recipe.imgUrlArray.find(x => x.includes('/recipe')) ??
                      recipe.imgUrlArray.find(x => x.includes('/suggestion')) ??
                      "/img/placeholder-food.jpg"
                    }
                    alt={recipe.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(recipe.complexity)}`}>
                      {getComplexityLabel(recipe.complexity)}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#87A96B] font-medium">
                      {recipe.dishType}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatTime(recipe.prepTime + recipe.cookTime)}
                    </div>
                  </div>
                  
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <Calendar className="w-3 h-3 mr-1" />
                    Réalisée {getTimeAgo(recipe.updatedAt)}
                  </div>
                  
                  <h3 className="text-lg sm:text-xl font-semibold text-[#2C3E50] mb-2">
                    {recipe.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {recipe.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {recipe.tags?.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-[#87A96B]/20 text-[#87A96B] rounded-full text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                    {recipe.tags?.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        +{recipe.tags.length - 3}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <Utensils className="w-4 h-4 mr-1" />
                      NutriScore: {recipe.nutriScore}/10
                    </div>
                    <button className="px-4 py-2 bg-gradient-to-r from-[#87A96B] to-[#87A96B]/90 text-white rounded-full hover:from-[#87A96B]/90 hover:to-[#87A96B]/80 transition-all text-sm font-medium">
                      Évaluer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && recipes.length === 0 && (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-600 mb-2">
              Aucune recette à évaluer
            </h2>
            <p className="text-gray-500 text-sm sm:text-base">
              Vous avez déjà évalué toutes vos recettes terminées !
            </p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
